import apiClient from '@/lib/apiClient';
import { ApiResponse, AuthSessionPayload, DeviceSession, User } from '@/types';

export interface VerifyOtpPayload {
  mobile: string;
  otp: string;
  rememberMe: boolean;
}

export interface RegisterPayload {
  name: string;
  mobile: string;
  email: string;
  jobRole: string;
  otp: string;
}

/**
 * Outcome of a login OTP request.
 *
 * `otpSent: false` is a SUCCESSFUL response, not an error: the number is inside
 * the resend cooldown (or a lockout), so nothing new was sent. The endpoint
 * answers identically for an unregistered number, so this still reveals nothing
 * about who has an account - it only stops the screen claiming a code is on its
 * way when none is.
 */
export interface SendOtpResult {
  otpSent: boolean;
  /** True when the wait is a lockout after wrong codes, not the resend gap. */
  locked: boolean;
  /** Seconds until another request will be honoured. Drives the resend timer. */
  retryAfterSeconds: number;
  /** Local development + terminal delivery only. */
  devOtp?: string;
}

const SEND_OTP_FALLBACK: SendOtpResult = { otpSent: true, locked: false, retryAfterSeconds: 30 };

export const authService = {
  // devOtp is only present in non-production (OTP_DELIVERY=TERMINAL); it lets the
  // login screen show the code locally instead of reading the server console.
  sendOtp: async (mobile: string): Promise<SendOtpResult> => {
    const { data } = await apiClient.post<ApiResponse<SendOtpResult>>('/auth/send-otp', { mobile });
    return data.data ?? SEND_OTP_FALLBACK;
  },

  verifyOtp: async (payload: VerifyOtpPayload): Promise<AuthSessionPayload> => {
    const { data } = await apiClient.post<ApiResponse<AuthSessionPayload>>(
      '/auth/verify-otp',
      payload,
    );
    if (!data.data) throw new Error('Login failed');
    return data.data;
  },

  // Step 1 of registration: send the verification OTP to the entered mobile.
  registerSendOtp: async (mobile: string): Promise<void> => {
    await apiClient.post<ApiResponse<{ otpSent: boolean }>>('/auth/register/send-otp', { mobile });
  },

  // Step 2: submit the full profile + OTP. Creates a PENDING account; no
  // session is returned (the user logs in only after admin approval).
  register: async (payload: RegisterPayload): Promise<User> => {
    const { data } = await apiClient.post<ApiResponse<{ user: User }>>('/auth/register', payload);
    if (!data.data) throw new Error('Registration failed');
    return data.data.user;
  },

  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  logoutAll: async (): Promise<void> => {
    await apiClient.post('/auth/logout-all');
  },

  getMe: async (): Promise<User> => {
    const { data } = await apiClient.get<ApiResponse<User>>('/auth/me');
    if (!data.data) throw new Error('Failed to load profile');
    return data.data;
  },

  listSessions: async (): Promise<DeviceSession[]> => {
    const { data } = await apiClient.get<ApiResponse<DeviceSession[]>>('/auth/sessions');
    return data.data ?? [];
  },

  revokeSession: async (id: number): Promise<void> => {
    await apiClient.delete(`/auth/sessions/${id}`);
  },
};
